import { useRef, type MouseEvent, type ReactNode } from "react";

type Props = {
    children: ReactNode;
    className?: string;
    maxTiltDeg?: number;
    scaleOnHover?: number;
};

/**
 * 3D tilt effect that follows the cursor. Resets smoothly on leave.
 * Uses CSS transforms directly — no JS-driven animation frames.
 */
export function TiltCard({
    children,
    className = "",
    maxTiltDeg = 8,
    scaleOnHover = 1.03,
}: Props) {
    const ref = useRef<HTMLDivElement>(null);

    const onMouseMove = (e: MouseEvent<HTMLDivElement>) => {
        const el = ref.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const px = (e.clientX - rect.left) / rect.width;  // 0..1
        const py = (e.clientY - rect.top) / rect.height;  // 0..1
        const tiltX = (py - 0.5) * -2 * maxTiltDeg;
        const tiltY = (px - 0.5) *  2 * maxTiltDeg;
        el.style.transform =
            `perspective(800px) rotateX(${tiltX}deg) rotateY(${tiltY}deg) scale(${scaleOnHover})`;
    };

    const onMouseLeave = () => {
        const el = ref.current;
        if (!el) return;
        el.style.transform = "perspective(800px) rotateX(0) rotateY(0) scale(1)";
    };

    return (
        <div
            ref={ref}
            onMouseMove={onMouseMove}
            onMouseLeave={onMouseLeave}
            className={`transition-transform duration-200 ease-out will-change-transform ${className}`}
            style={{ transformStyle: "preserve-3d" }}
        >
            {children}
        </div>
    );
}
